import { useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { useCart } from "@/App";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetFooter } from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import RelatedProducts from "./RelatedProducts";

export default function CartDrawer({ children }) {
  const { cart, updateQuantity, removeFromCart, cartTotal, cartCount } = useCart();
  const navigate = useNavigate();

  return (
    <Sheet>
      <SheetTrigger asChild>{children}</SheetTrigger>
      <SheetContent className="w-full sm:max-w-lg flex flex-col">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <ShoppingBag className="h-5 w-5 text-emerald-600" />
            Your Cart ({cartCount} items)
          </SheetTitle>
        </SheetHeader>

        {cart.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center py-12">
            <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mb-4">
              <ShoppingBag className="h-10 w-10 text-slate-400" />
            </div>
            <h3 className="text-lg font-medium text-slate-900 mb-2">Your cart is empty</h3>
            <p className="text-slate-500 text-sm mb-6">Add some delicious items to get started!</p>
            <SheetTrigger asChild>
              <Button 
                onClick={() => navigate("/products")}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="start-shopping-btn"
              >
                Start Shopping
              </Button>
            </SheetTrigger>
          </div>
        ) : (
          <>
            <ScrollArea className="flex-1 -mx-6 px-6">
              <div className="space-y-4 py-4">
                {cart.map((item, index) => (
                  <div 
                    key={`${item.product_id}-${item.weight_option}-${index}`}
                    className="flex gap-4 p-3 bg-slate-50 rounded-xl cart-item-enter"
                    data-testid={`cart-item-${item.product_id}`}
                  >
                    {/* Product image */}
                    <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 bg-white">
                      {item.image_url ? (
                        <img
                          src={item.image_url}
                          alt={item.product_name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-slate-200 flex items-center justify-center">
                          <ShoppingBag className="h-8 w-8 text-slate-400" />
                        </div>
                      )}
                    </div>

                    {/* Product details */}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-slate-900 truncate">{item.product_name}</h4>
                      {item.weight_option && (
                        <p className="text-sm text-slate-500">{item.weight_option}</p>
                      )}
                      <p className="text-emerald-600 font-semibold mt-1">
                        £{item.price.toFixed(2)} / {item.unit}
                      </p>

                      {/* Quantity controls */}
                      <div className="flex items-center gap-3 mt-2">
                        <div className="flex items-center border border-slate-200 rounded-full bg-white">
                          <button
                            onClick={() => updateQuantity(item.product_id, item.quantity - 1, item.weight_option)}
                            className="w-8 h-8 flex items-center justify-center text-slate-600 hover:text-emerald-600 qty-btn"
                            data-testid={`decrease-qty-${item.product_id}`}
                          >
                            <Minus className="h-4 w-4" />
                          </button>
                          <span className="w-8 text-center font-medium text-sm" data-testid={`qty-${item.product_id}`}>
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => updateQuantity(item.product_id, item.quantity + 1, item.weight_option)}
                            className="w-8 h-8 flex items-center justify-center text-slate-600 hover:text-emerald-600 qty-btn"
                            data-testid={`increase-qty-${item.product_id}`}
                          >
                            <Plus className="h-4 w-4" />
                          </button>
                        </div>

                        <button
                          onClick={() => removeFromCart(item.product_id, item.weight_option)}
                          className="text-red-500 hover:text-red-600 p-1"
                          data-testid={`remove-item-${item.product_id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    {/* Item total */}
                    <div className="text-right">
                      <p className="font-semibold text-slate-900">
                        £{(item.price * item.quantity).toFixed(2)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Frequently bought together - anchored to most recent cart item */}
              {cart.length > 0 && (
                <div className="px-1 pb-4">
                  <RelatedProducts
                    productId={cart[cart.length - 1].product_id}
                    title="Frequently bought together"
                  />
                </div>
              )}
            </ScrollArea>

            <div className="border-t pt-4 space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-slate-600">Subtotal</span>
                <span className="font-semibold text-lg" data-testid="cart-subtotal">
                  £{cartTotal.toFixed(2)}
                </span>
              </div>
              <p className="text-sm text-slate-500">Pickup is free - pay at collection or online</p>
              
              <SheetTrigger asChild>
                <Button
                  onClick={() => navigate("/checkout")}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 h-12 text-base font-medium rounded-xl"
                  data-testid="checkout-btn"
                >
                  Proceed to Checkout
                </Button>
              </SheetTrigger>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
